import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Collection, type Project, type Workspace, type RequestSummary, type Webhook, type Flow, type User } from "../lib/api";
import Spinner, { InlineLoading } from "../components/Spinner";
import { copyToClipboard } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import { usePrompt } from "../components/PromptDialog";
import RequestBuilder from "../components/RequestBuilder";
import WebhookBuilder from "../components/WebhookBuilder";
import FlowBuilder from "../components/FlowBuilder";
import OpenApiImport from "../components/OpenApiImport";
import ImportModal from "../components/ImportModal";
import CurlImport from "../components/CurlImport";
import TabStrip from "../components/TabStrip";
import UserMenu from "../components/UserMenu";
import { useOpenTabs, closeOpenTabFor } from "../lib/openTabs";
import { useRenameRequest } from "../lib/renameRequest";
import { useResizable } from "../lib/useResizable";
import { useHotkey } from "../lib/keybindings";
import SortableTree from "../components/SortableTree";

export default function WorkspacePage() {
  const { workspace, project, user } = useOutletContext<{ workspace: Workspace; project: Project | null; user: User }>();
  const params = useParams();
  const requestId = params.requestId ?? null;
  const webhookId = params.webhookId ?? null;
  const flowId = params.flowId ?? null;
  // Tab IDs are namespaced — `r:abc` requests, `w:abc` webhooks,
  // `f:abc` flows — so a single open-tab list can hold all three kinds.
  const activeTabId = flowId ? `f:${flowId}` : webhookId ? `w:${webhookId}` : requestId ? `r:${requestId}` : null;
  const { tabs, close: closeTab, setTabs } = useOpenTabs(workspace.id, activeTabId);

  // Mobile shows ONE pane at a time: the sidebar when nothing is open,
  // the builder when something is. The MobileTopbar's back button (see
  // Layout.tsx) navigates to "/" which flips back to the list. Desktop
  // (sm+) always shows both, exactly as before.
  const hasSelection = !!(flowId || webhookId || requestId);
  return (
    <div className="h-full flex min-h-0">
      <CollectionsPane
        workspace={workspace}
        project={project}
        user={user}
        selectedRequestId={requestId}
        selectedWebhookId={webhookId}
        selectedFlowId={flowId}
        hiddenOnMobile={hasSelection}
      />
      <div className={"flex-1 min-w-[60px] flex-col " + (hasSelection ? "flex" : "hidden sm:flex")}>
        <TabStrip tabs={tabs} activeId={activeTabId} onClose={closeTab} onReorder={setTabs} />
        <div className="flex-1 min-h-0">
          {flowId ? (
            <FlowBuilder flowId={flowId} workspaceId={workspace.id} />
          ) : webhookId ? (
            <WebhookBuilder webhookId={webhookId} workspaceId={workspace.id} />
          ) : requestId ? (
            <RequestBuilder requestId={requestId} workspaceId={workspace.id} />
          ) : (
            <EmptyState workspace={workspace} />
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ workspace }: { workspace: Workspace }) {
  const logoSrc = workspace.logo_updated_at
    ? api.avatars.workspaceUrl(workspace.id, workspace.logo_updated_at)
    : null;
  return (
    <div className="grid h-full place-items-center p-8">
      <div className="text-center max-w-md space-y-3">
        {logoSrc ? (
          <img src={logoSrc} alt="" className="mx-auto h-16 w-16 rounded-2xl object-cover" />
        ) : (
          <div className="mx-auto h-14 w-14 grid place-items-center rounded-2xl bg-surface-2 text-text-faint">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M4 7a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            </svg>
          </div>
        )}
        <h2 className="text-lg font-semibold">Welcome to {workspace.name}</h2>
        <p className="text-sm muted">Open a saved item from the sidebar, or create a collection to start saving requests.</p>
      </div>
    </div>
  );
}

type SidebarTab = "requests" | "webhooks" | "flows";

const sidebarTabKey = (wid: string) => `rest.sidebar.tab:${wid}`;
function readStoredSidebarTab(workspaceId: string): SidebarTab {
  try {
    const v = localStorage.getItem(sidebarTabKey(workspaceId));
    return v === "webhooks" || v === "flows" ? v : "requests";
  } catch { return "requests"; }
}
function writeStoredSidebarTab(workspaceId: string, t: SidebarTab): void {
  try { localStorage.setItem(sidebarTabKey(workspaceId), t); } catch { /* ignore */ }
}

function CollectionsPane({ workspace, project, user, selectedRequestId, selectedWebhookId, selectedFlowId, hiddenOnMobile }: { workspace: Workspace; project: Project | null; user: User; selectedRequestId: string | null; selectedWebhookId: string | null; selectedFlowId: string | null; hiddenOnMobile?: boolean }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [importOpen, setImportOpen] = useState(false);
  const { size: paneWidth, dragProps: paneDrag } = useResizable({
    axis: "x", initial: 288, min: 60, max: 560, storageKey: "rest.sidebar.collections.width",
  });

  // Sidebar tab — Requests / Webhooks / Flows. Navigating to a typed
  // page (e.g. /w/<id>) auto-switches the tab so the active item is
  // visible. User clicks override and persist via localStorage so the
  // choice survives reloads when no item is selected.
  const [activeTab, setActiveTab] = useState<SidebarTab>(() => {
    if (selectedWebhookId) return "webhooks";
    if (selectedFlowId) return "flows";
    if (selectedRequestId) return "requests";
    return readStoredSidebarTab(workspace.id);
  });
  useEffect(() => {
    if (selectedWebhookId) setActiveTab("webhooks");
    else if (selectedFlowId) setActiveTab("flows");
    else if (selectedRequestId) setActiveTab("requests");
  }, [selectedWebhookId, selectedFlowId, selectedRequestId]);
  // When the workspace changes (rare), reload the stored tab.
  useEffect(() => {
    if (!selectedRequestId && !selectedWebhookId && !selectedFlowId) {
      setActiveTab(readStoredSidebarTab(workspace.id));
    }
  }, [workspace.id]);
  const onTabClick = (t: SidebarTab) => {
    setActiveTab(t);
    writeStoredSidebarTab(workspace.id, t);
  };

  // Brief spinner when switching modes so the UI feels deliberate. 220ms
  // min covers the swap even when the next list is already cached and
  // the React reconcile completes in <16ms.
  const [tabSwitching, setTabSwitching] = useState(false);
  const prevTabRef = useRef<SidebarTab>(activeTab);
  useEffect(() => {
    if (prevTabRef.current === activeTab) return;
    prevTabRef.current = activeTab;
    setTabSwitching(true);
    const t = setTimeout(() => setTabSwitching(false), 220);
    return () => clearTimeout(t);
  }, [activeTab]);
  // Mirror the sidebar's current width into a CSS variable so the
  // drag-clone (rendered by dnd-kit in a portal at document.body) can
  // match it. The clone's `width: var(...)` rule lives in styles.css.
  useEffect(() => {
    document.documentElement.style.setProperty("--rest-sidebar-width", `${paneWidth}px`);
  }, [paneWidth]);

  const collectionsQ = useQuery({
    queryKey: ["collections", workspace.id, project?.id ?? "_none"],
    queryFn: () => api.collections.list(workspace.id, project?.id ?? null),
  });

  // A project has exactly one collection from the UI's point of view. If
  // none exists yet for this project, auto-create one named "Requests" the
  // first time the panel loads.
  const ensureCol = useMutation({
    mutationFn: () => api.collections.create({ workspace_id: workspace.id, project_id: project?.id, name: "Requests" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collections", workspace.id, project?.id ?? "_none"] }),
  });
  useEffect(() => {
    if (collectionsQ.isSuccess && (collectionsQ.data?.collections.length ?? 0) === 0 && !ensureCol.isPending) {
      ensureCol.mutate();
    }
  }, [collectionsQ.isSuccess, collectionsQ.data, ensureCol]);

  // The primary collection is the first one in the list (for legacy projects
  // with multiple collections, the others remain in the DB but the UI
  // surfaces a single tree per project).
  const primary = collectionsQ.data?.collections[0] ?? null;

  // Realtime filter — matches request name + url + folder name.
  const [filter, setFilter] = useState("");

  // Cmd/Ctrl+B creates a new request in the active collection and jumps
  // straight to its editor. Mirrors the "+" button next to the filter.
  useHotkey("Mod+b", async () => {
    if (!primary) return;
    const r = await api.requests.create({ collection_id: primary.id, name: "Untitled request", method: "GET", url: "" });
    qc.invalidateQueries({ queryKey: ["requests", primary.id] });
    nav(`/r/${r.id}`);
  });

  return (
    <aside
      className={
        "collections-pane shrink-0 border-r border-border bg-surface flex-col min-h-0 relative overflow-hidden " +
        (hiddenOnMobile ? "hidden sm:flex" : "flex")
      }
      style={{ width: paneWidth }}
    >
      {/* Tab bar — three peers (Requests / Webhooks / Flows) with a
          per-tab "+" action on the right. Height matches the editor's
          tab strip (h-9) so the horizontal line across both columns
          stays clean. */}
      <div className="h-9 pl-3 pr-2 flex items-center border-b border-border">
        <SidebarModeDropdown activeTab={activeTab} onPick={onTabClick} />
        {primary && (
          <div className="ml-auto flex items-center">
            {activeTab === "requests" && (
              <NewRequestMenu
                onNewRequest={async () => {
                  const r = await api.requests.create({ collection_id: primary.id, name: "Untitled request", method: "GET", url: "" });
                  qc.invalidateQueries({ queryKey: ["requests", primary.id] });
                  nav(`/r/${r.id}`);
                }}
                onNewFolder={async () => {
                  const name = await prompt({ title: "New folder", placeholder: "Folder name", confirmLabel: "Create", inputLabel: "Name" });
                  if (name) {
                    await api.folders.create({ collection_id: primary.id, parent_folder_id: null, name });
                    qc.invalidateQueries({ queryKey: ["folders", primary.id] });
                  }
                }}
                onImport={() => setImportOpen(true)}
              />
            )}
            {activeTab === "webhooks" && (
              <button
                type="button"
                title="New webhook"
                aria-label="New webhook"
                className="h-6 w-6 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
                onClick={async () => {
                  const w = await api.webhooks.create({ collection_id: primary.id, name: "Untitled webhook" });
                  qc.invalidateQueries({ queryKey: ["webhooks", primary.id] });
                  nav(`/w/${w.id}`);
                }}
              ><PlusIcon /></button>
            )}
            {activeTab === "flows" && (
              <button
                type="button"
                title="New flow"
                aria-label="New flow"
                className="h-6 w-6 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
                onClick={async () => {
                  const f = await api.flows.create({ collection_id: primary.id, name: "Untitled flow" });
                  qc.invalidateQueries({ queryKey: ["flows", primary.id] });
                  nav(`/f/${f.id}`);
                }}
              ><PlusIcon /></button>
            )}
          </div>
        )}
      </div>
      <div className="px-2 py-2 border-b border-border">
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-text-faint pointer-events-none">
            <SmallSearchIcon />
          </span>
          <input
            className="input w-full !pl-7 !py-1 !text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={
              activeTab === "webhooks" ? "Filter webhooks…" :
              activeTab === "flows" ? "Filter flows…" :
              "Filter requests…"
            }
            aria-label={`Filter ${activeTab}`}
            spellCheck={false}
          />
          {filter && (
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-3"
              onClick={() => setFilter("")}
              aria-label="Clear filter"
            >×</button>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
        {!primary && <div className="p-4"><InlineLoading /></div>}
        {tabSwitching && (
          <div className="flex-1 min-h-0 grid place-items-center">
            <Spinner size={20} className="text-text-faint" />
          </div>
        )}
        <ul className={"flex-1 min-h-0 flex flex-col py-1 " + (tabSwitching ? "hidden" : "")}>
          {primary && (
            <CollectionNode
              key={primary.id}
              collection={primary}
              expanded={true}
              onToggle={() => { /* always expanded in flat mode */ }}
              selectedRequestId={selectedRequestId}
              selectedWebhookId={selectedWebhookId}
              selectedFlowId={selectedFlowId}
              filter={filter.trim().toLowerCase()}
              flat={true}
              activeTab={activeTab}
              onDelete={async () => { /* no-op in flat mode */ }}
              onCreateRequest={(folderId) => {
                api.requests.create({ collection_id: primary.id, name: "Untitled request", method: "GET", url: "" }).then(async (r) => {
                  if (folderId) {
                    await api.requests.update(r.id, { folder_id: folderId });
                  }
                  qc.invalidateQueries({ queryKey: ["requests", primary.id] });
                  qc.invalidateQueries({ queryKey: ["folders", primary.id] });
                  nav(`/r/${r.id}`);
                });
              }}
              onCreateWebhook={(folderId) => {
                api.webhooks.create({ collection_id: primary.id, name: "Untitled webhook", folder_id: folderId }).then(async (w) => {
                  qc.invalidateQueries({ queryKey: ["webhooks", primary.id] });
                  qc.invalidateQueries({ queryKey: ["folders", primary.id] });
                  nav(`/w/${w.id}`);
                });
              }}
              onCreateFlow={(folderId) => {
                api.flows.create({ collection_id: primary.id, name: "Untitled flow", folder_id: folderId ?? null }).then(async (f) => {
                  qc.invalidateQueries({ queryKey: ["flows", primary.id] });
                  qc.invalidateQueries({ queryKey: ["folders", primary.id] });
                  nav(`/f/${f.id}`);
                });
              }}
            />
          )}
        </ul>
      </div>
      <div className="border-t border-border p-2 shrink-0">
        <UserMenu user={user} />
      </div>
      <div
        {...paneDrag}
        title="Drag to resize"
        aria-label="Resize sidebar"
        className="absolute top-0 right-[-3px] bottom-0 w-1.5 cursor-col-resize bg-transparent hover:bg-accent active:bg-accent-bright transition-colors z-10"
      />
      {primary && (
        <ImportModal collectionId={primary.id} open={importOpen} onClose={() => setImportOpen(false)} />
      )}
    </aside>
  );
}

function SmallSearchIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.8" />
      <path d="M16 16l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

interface FolderNode {
  id: string;
  parent_folder_id: string | null;
  name: string;
  sort_order?: number;
}

function CollectionNode({
  collection,
  expanded,
  onToggle,
  selectedRequestId,
  selectedWebhookId,
  selectedFlowId,
  filter,
  flat = false,
  activeTab,
  onDelete,
  onCreateRequest,
  onCreateWebhook,
  onCreateFlow,
}: {
  collection: Collection;
  expanded: boolean;
  onToggle: () => void;
  selectedRequestId: string | null;
  selectedWebhookId: string | null;
  selectedFlowId: string | null;
  filter: string;
  flat?: boolean;
  activeTab?: SidebarTab;
  onDelete: () => void;
  onCreateRequest: (folderId: string | null) => void;
  onCreateWebhook: (folderId: string | null) => void;
  onCreateFlow: (folderId: string | null) => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const reqQ = useQuery({
    queryKey: ["requests", collection.id],
    queryFn: () => api.collections.requests(collection.id),
    enabled: expanded,
  });
  const foldersQ = useQuery({
    queryKey: ["folders", collection.id],
    queryFn: () => api.folders.list(collection.id),
    enabled: expanded,
  });
  // Webhooks live in the same sidebar tree as requests — merged into the
  // SortableTree below by folder_id + sort_order.
  const webhooksQ = useQuery({
    queryKey: ["webhooks", collection.id],
    queryFn: () => api.webhooks.listByCollection(collection.id),
    enabled: expanded,
  });
  const flowsQ = useQuery({
    queryKey: ["flows", collection.id],
    queryFn: () => api.flows.listByCollection(collection.id),
    enabled: expanded,
  });

  const [openApiOpen, setOpenApiOpen] = useState(false);
  const [curlOpenFor, setCurlOpenFor] = useState<string | null | undefined>(undefined); // undefined=closed, null=root, "fld_…"=folder
  const rename = useMutation({
    mutationFn: (name: string) => api.collections.update(collection.id, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["collections", collection.workspace_id] }),
  });
  const askRename = async () => {
    const name = await prompt({
      title: "Rename collection",
      initialValue: collection.name,
      placeholder: "Collection name",
      confirmLabel: "Rename",
      inputLabel: "Name",
    });
    if (name && name !== collection.name) rename.mutate(name);
  };

  const createFolder = useMutation({
    mutationFn: (args: { parentId: string | null; name: string }) => api.folders.create({ collection_id: collection.id, parent_folder_id: args.parentId, name: args.name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["folders", collection.id] }),
  });
  const askCreateFolder = async (parentId: string | null) => {
    const name = await prompt({
      title: parentId ? "New subfolder" : "New folder",
      placeholder: "Folder name",
      confirmLabel: "Create",
      inputLabel: "Name",
    });
    if (name) createFolder.mutate({ parentId, name });
  };
  const removeFolder = useMutation({
    mutationFn: (id: string) => api.folders.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["folders", collection.id] });
      qc.invalidateQueries({ queryKey: ["requests", collection.id] });
    },
  });
  const renameFolder = useMutation({
    mutationFn: (args: { id: string; name: string }) => api.folders.update(args.id, { name: args.name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["folders", collection.id] }),
  });
  const askRenameFolder = async (id: string, current: string) => {
    const name = await prompt({
      title: "Rename folder",
      initialValue: current,
      placeholder: "Folder name",
      confirmLabel: "Rename",
      inputLabel: "Name",
    });
    if (name && name !== current) renameFolder.mutate({ id, name });
  };

  const requests = useMemo(() => reqQ.data?.requests ?? [], [reqQ.data]);
  const folders = useMemo<FolderNode[]>(() => foldersQ.data?.folders ?? [], [foldersQ.data]);
  const webhooks = useMemo<Webhook[]>(() => webhooksQ.data?.webhooks ?? [], [webhooksQ.data]);
  const flows = useMemo<Flow[]>(() => flowsQ.data?.flows ?? [], [flowsQ.data]);

  // Download the collection as OpenAPI, used from the toolbar menu.
  const exportOpenApi = () => {
    (async () => {
      const res = await fetch(`/api/openapi/export/${collection.id}?download=1`, { credentials: "include" });
      if (!res.ok) return;
      const blob = await res.blob();
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      const cd = res.headers.get("content-disposition") ?? "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : `${collection.name}.openapi.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    })();
  };

  return (
    <li className="flex-1 min-h-0 flex flex-col">
      {!flat && (
        <div className="group flex items-center gap-1 px-2 py-1 hover:bg-surface-2">
          <button
            type="button"
            onClick={onToggle}
            className="h-5 w-5 grid place-items-center rounded text-text-faint hover:text-text"
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            <Chevron expanded={expanded} />
          </button>
          <button
            type="button"
            onClick={onToggle}
            onDoubleClick={askRename}
            className="flex-1 min-w-0 text-left text-sm font-medium truncate text-text"
            title={collection.name}
          >
            {collection.name}
          </button>
          <button
            type="button"
            onClick={() => askCreateFolder(null)}
            className="h-7 w-7 sm:h-6 sm:w-6 grid place-items-center rounded text-text-faint sm:opacity-0 sm:group-hover:opacity-100 hover:text-text hover:bg-surface-3"
            title="New top-level folder"
          >
            <FolderPlusIcon />
          </button>
          <button
            type="button"
            onClick={() => onCreateRequest(null)}
            className="h-7 w-7 sm:h-6 sm:w-6 grid place-items-center rounded text-text-faint sm:opacity-0 sm:group-hover:opacity-100 hover:text-text hover:bg-surface-3"
            title="New request"
          >
            <PlusIcon />
          </button>
          <CollectionMenu
            onRename={askRename}
            onDelete={onDelete}
            onOpenApi={() => setOpenApiOpen(true)}
            onImportCurl={() => setCurlOpenFor(null)}
            onExportOpenApi={exportOpenApi}
          />
        </div>
      )}
      {openApiOpen && <OpenApiImport collectionId={collection.id} onClose={() => setOpenApiOpen(false)} />}
      {curlOpenFor !== undefined && (
        <CurlImport collectionId={collection.id} folderId={curlOpenFor} onClose={() => setCurlOpenFor(undefined)} />
      )}
      {expanded && (!flat || activeTab === "requests") && (
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scroll-pane">
          {!flat && (
            <div className="px-2 pt-2 pb-1 flex items-center justify-between gap-2 sticky top-0 bg-surface z-[1]">
              <div className="text-[10px] uppercase tracking-wider muted-strong font-semibold">
                Requests <span className="muted">· {requests.length}</span>
              </div>
            </div>
          )}
          {flat && requests.length === 0 && folders.length === 0 && (
            <div className="px-3 py-6 text-center text-xs muted italic">No requests yet.</div>
          )}
          <SortableTree
          folders={folders}
          requests={requests}
          // Webhooks live in their own section below, not inside the
          // request tree. Passing an empty list here keeps the tree
          // focused on folders + requests; the move handler still
          // routes webhook-flavoured updates through api.webhooks.move
          // so we don't lose anything if drag/drop later returns.
          webhooks={[]}
          selectedRequestId={selectedRequestId}
          selectedWebhookId={selectedWebhookId}
          filter={filter}
          onMove={(updates) => {
            // The tree emits an update for EVERY visible node after a
            // drop. Dedupe against current state and only PATCH items
            // whose parent or sort_order actually changed. Webhooks
            // route to api.webhooks.move; requests to api.requests.move;
            // folders to api.folders.update.
            const folderById = new Map(folders.map((f) => [f.id, f]));
            const requestById = new Map(requests.map((r) => [r.id, r as RequestSummary & { folder_id?: string | null }]));
            const webhookById = new Map(webhooks.map((w) => [w.id, w]));
            const reqPatches: Array<{ id: string; folder_id: string | null; sort_order: number }> = [];
            const folPatches: Array<{ id: string; parent_folder_id: string | null; sort_order: number }> = [];
            const whPatches: Array<{ id: string; folder_id: string | null; sort_order: number }> = [];
            for (const u of updates) {
              if (u.kind === "request") {
                const cur = requestById.get(u.rawId);
                if (!cur) continue;
                const sameParent = (cur.folder_id ?? null) === u.parentId;
                const sameOrder = (cur.sort_order ?? 0) === u.sortOrder;
                if (sameParent && sameOrder) continue;
                reqPatches.push({ id: u.rawId, folder_id: u.parentId, sort_order: u.sortOrder });
              } else if (u.kind === "webhook") {
                const cur = webhookById.get(u.rawId);
                if (!cur) continue;
                const sameParent = (cur.folder_id ?? null) === u.parentId;
                const sameOrder = (cur.sort_order ?? 0) === u.sortOrder;
                if (sameParent && sameOrder) continue;
                whPatches.push({ id: u.rawId, folder_id: u.parentId, sort_order: u.sortOrder });
              } else {
                const cur = folderById.get(u.rawId);
                if (!cur) continue;
                const sameParent = (cur.parent_folder_id ?? null) === u.parentId;
                const sameOrder = (cur.sort_order ?? 0) === u.sortOrder;
                if (sameParent && sameOrder) continue;
                folPatches.push({ id: u.rawId, parent_folder_id: u.parentId, sort_order: u.sortOrder });
              }
            }
            // Optimistic cache updates so the UI doesn't snap back.
            if (reqPatches.length) {
              qc.setQueryData<{ requests: RequestSummary[] }>(["requests", collection.id], (old) => {
                if (!old) return old;
                const m = new Map(reqPatches.map((p) => [p.id, p]));
                return {
                  requests: old.requests.map((r) => {
                    const p = m.get(r.id);
                    return p
                      ? ({ ...(r as RequestSummary & { folder_id?: string | null }), folder_id: p.folder_id, sort_order: p.sort_order } as RequestSummary)
                      : r;
                  }),
                };
              });
            }
            if (whPatches.length) {
              qc.setQueryData<{ webhooks: Webhook[] }>(["webhooks", collection.id], (old) => {
                if (!old) return old;
                const m = new Map(whPatches.map((p) => [p.id, p]));
                return {
                  webhooks: old.webhooks.map((w) => {
                    const p = m.get(w.id);
                    return p ? { ...w, folder_id: p.folder_id, sort_order: p.sort_order } : w;
                  }),
                };
              });
            }
            if (folPatches.length) {
              qc.setQueryData<{ folders: FolderNode[] }>(["folders", collection.id], (old) => {
                if (!old) return old;
                const m = new Map(folPatches.map((p) => [p.id, p]));
                return {
                  folders: old.folders.map((f) => {
                    const p = m.get(f.id);
                    return p ? { ...f, parent_folder_id: p.parent_folder_id, sort_order: p.sort_order } : f;
                  }),
                };
              });
            }
            // Fire all PATCHes in parallel.
            Promise.all([
              ...reqPatches.map((p) => api.requests.move(p.id, { folder_id: p.folder_id, sort_order: p.sort_order })),
              ...whPatches.map((p) => api.webhooks.move(p.id, { folder_id: p.folder_id, sort_order: p.sort_order })),
              ...folPatches.map((p) => api.folders.update(p.id, { parent_folder_id: p.parent_folder_id, sort_order: p.sort_order })),
            ]).then(() => {
              qc.invalidateQueries({ queryKey: ["requests", collection.id] });
              qc.invalidateQueries({ queryKey: ["webhooks", collection.id] });
              qc.invalidateQueries({ queryKey: ["folders", collection.id] });
            });
          }}
          renderRow={(args) =>
            args.kind === "folder" ? (
              <FolderRowInner
                rawId={args.rawId}
                name={args.name}
                isExpanded={args.isExpanded}
                onToggle={args.onToggle}
                onAskRenameFolder={askRenameFolder}
                onAskCreateFolder={askCreateFolder}
                onCreateRequest={onCreateRequest}
                onImportCurl={(fid) => setCurlOpenFor(fid)}
                onDeleteFolder={async (fid, name) => {
                  const ok = await confirm({ title: `Delete folder "${name}"?`, description: "Requests and webhooks inside the folder move to the collection root.", danger: true });
                  if (ok) removeFolder.mutate(fid);
                }}
              />
            ) : args.kind === "webhook" ? (
              <WebhookRowInner
                rawId={args.rawId}
                name={args.name}
                token={args.token ?? ""}
                requestCount={args.requestCount ?? 0}
                selected={args.selected}
                workspaceId={collection.workspace_id}
                collectionId={collection.id}
              />
            ) : (
              <RequestRowInner
                rawId={args.rawId}
                name={args.name}
                method={args.method}
                url={args.url}
                selected={args.selected}
                workspaceId={collection.workspace_id}
              />
            )
          }
        />
        </div>
      )}
      {/* Webhooks tab — flat list of inbound capture endpoints. */}
      {expanded && (!flat || activeTab === "webhooks") && (!flat || webhooks.length > 0 || activeTab === "webhooks") && (
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scroll-pane">
          {webhooks.length === 0 && activeTab === "webhooks" && flat ? (
            <div className="px-3 py-6 text-center text-xs muted italic">No webhooks yet.</div>
          ) : (
            <WebhookSection
              webhooks={webhooks}
              filter={filter}
              selectedWebhookId={selectedWebhookId}
              workspaceId={collection.workspace_id}
              collectionId={collection.id}
              onAddWebhook={() => onCreateWebhook(null)}
              hideHeader={flat && activeTab === "webhooks"}
            />
          )}
        </div>
      )}
      {/* Flows tab — flat list. flows.folder_id is preserved in the
          schema for later nesting. */}
      {expanded && (!flat || activeTab === "flows") && (!flat || flows.length > 0 || activeTab === "flows") && (
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scroll-pane">
          {flows.length === 0 && activeTab === "flows" && flat ? (
            <div className="px-3 py-6 text-center text-xs muted italic">No flows yet.</div>
          ) : (
            <FlowSection
              flows={flows}
              filter={filter}
              selectedFlowId={selectedFlowId}
              workspaceId={collection.workspace_id}
              collectionId={collection.id}
              onAddFlow={() => onCreateFlow(null)}
              hideHeader={flat && activeTab === "flows"}
            />
          )}
        </div>
      )}
    </li>
  );
}

// Flat "Webhooks" list rendered under the request tree. Mirrors the
// styling vocabulary of the tree's section header so the two read as
// peers in the sidebar.
function WebhookSection({
  webhooks, filter, selectedWebhookId, workspaceId, collectionId, onAddWebhook, hideHeader = false,
}: {
  webhooks: Webhook[];
  filter: string;
  selectedWebhookId: string | null;
  workspaceId: string;
  collectionId: string;
  onAddWebhook: () => void;
  hideHeader?: boolean;
}) {
  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? webhooks.filter((w) =>
        w.name.toLowerCase().includes(needle) ||
        w.token.toLowerCase().includes(needle))
    : webhooks;
  return (
    <div className={hideHeader ? "" : "pt-2"}>
      {!hideHeader && (
        <div className="px-2 pb-1 flex items-center justify-between gap-2 sticky top-0 bg-surface z-[1]">
          <div className="text-[10px] uppercase tracking-wider muted-strong font-semibold">
            Webhooks <span className="muted">· {webhooks.length}</span>
          </div>
          <button
            type="button"
            onClick={onAddWebhook}
            title="New webhook"
            aria-label="New webhook"
            className="h-5 w-5 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >＋</button>
        </div>
      )}
      {visible.length === 0 && needle && (
        <div className="px-2 py-1 text-xs muted italic">No matching webhooks.</div>
      )}
      <ul>
        {visible.map((w) => (
          <li key={w.id} className="px-1">
            <WebhookRowInner
              rawId={w.id}
              name={w.name}
              token={w.token}
              requestCount={w.request_count}
              selected={w.id === selectedWebhookId}
              workspaceId={workspaceId}
              collectionId={collectionId}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// Flat "Flows" list rendered below the webhook section. Same vocabulary
// as WebhookSection so the three sidebar sections read as peers.
function FlowSection({
  flows, filter, selectedFlowId, workspaceId, collectionId, onAddFlow, hideHeader = false,
}: {
  flows: Flow[];
  filter: string;
  selectedFlowId: string | null;
  workspaceId: string;
  collectionId: string;
  onAddFlow: () => void;
  hideHeader?: boolean;
}) {
  const needle = filter.trim().toLowerCase();
  const visible = needle ? flows.filter((f) => f.name.toLowerCase().includes(needle)) : flows;
  return (
    <div className={hideHeader ? "" : "pt-2"}>
      {!hideHeader && (
        <div className="px-2 pb-1 flex items-center justify-between gap-2 sticky top-0 bg-surface z-[1]">
          <div className="text-[10px] uppercase tracking-wider muted-strong font-semibold">
            Flows <span className="muted">· {flows.length}</span>
          </div>
          <button
            type="button"
            onClick={onAddFlow}
            title="New flow"
            aria-label="New flow"
            className="h-5 w-5 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >＋</button>
        </div>
      )}
      {visible.length === 0 && needle && (
        <div className="px-2 py-1 text-xs muted italic">No matching flows.</div>
      )}
      <ul>
        {visible.map((f) => (
          <li key={f.id} className="px-1">
            <FlowRowInner
              rawId={f.id}
              name={f.name}
              stepCount={f.steps?.length ?? 0}
              selected={f.id === selectedFlowId}
              workspaceId={workspaceId}
              collectionId={collectionId}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// Sidebar row for a flow. Branch glyph distinguishes it from request
// rows (method tag) and webhook rows (lightning bolt).
function FlowRowInner({ rawId, name, stepCount, selected, workspaceId, collectionId }: {
  rawId: string;
  name: string;
  stepCount: number;
  selected: boolean;
  workspaceId: string;
  collectionId: string;
}) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const remove = useMutation({
    mutationFn: () => api.flows.remove(rawId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flows", collectionId] });
      const { search, nextActive } = closeOpenTabFor(workspaceId, `f:${rawId}`);
      if (selected) {
        let path = "/";
        if (nextActive) {
          if (nextActive.startsWith("w:")) path = `/w/${nextActive.slice(2)}`;
          else if (nextActive.startsWith("f:")) path = `/f/${nextActive.slice(2)}`;
          else if (nextActive.startsWith("r:")) path = `/r/${nextActive.slice(2)}`;
          else path = `/r/${nextActive}`;
        }
        nav({ pathname: path, search });
      } else if (search !== window.location.search) {
        nav({ pathname: window.location.pathname, search }, { replace: true });
      }
    },
  });
  const renameMut = useMutation({
    mutationFn: (next: string) => api.flows.update(rawId, { name: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flow", rawId] });
      qc.invalidateQueries({ queryKey: ["flows", collectionId] });
    },
  });
  const askRename = async () => {
    const next = await prompt({ title: "Rename flow", placeholder: "Flow name", initialValue: name, confirmLabel: "Rename", inputLabel: "Name" });
    if (next && next !== name) renameMut.mutate(next);
  };
  return (
    <div
      className={
        "group flex items-center gap-2 px-1 py-0.5 rounded text-sm w-full min-w-0 cursor-pointer " +
        (selected ? "bg-accent-soft text-accent-bright" : "text-text-dim")
      }
      onClick={useSingleOrDoubleClick(
        () => nav({ pathname: `/f/${rawId}`, search: window.location.search }),
        askRename,
      )}
      title={`${name} — double-click to rename`}
    >
      <span className="shrink-0 text-accent" aria-hidden="true"><FlowIcon /></span>
      <span className="flex-1 min-w-0 truncate">{name}</span>
      <RowMenu
        onRename={askRename}
        onDelete={async () => {
          const ok = await confirm({ title: `Delete "${name}"?`, description: "This permanently removes the flow and all its runs.", danger: true });
          if (ok) remove.mutate();
        }}
      />
      {stepCount > 0 && (
        <span className="chip bg-surface-3 muted-strong shrink-0 text-[10px]">{stepCount}</span>
      )}
    </div>
  );
}

function FlowIcon() {
  // Three connected nodes in a Z-pattern — reads as a multi-step
  // pipeline. Distinct from the request method tag + webhook bolt.
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7.5 6 L16.5 11M16.5 13 L7.5 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="5" cy="5" r="3" fill="currentColor" />
      <circle cx="19" cy="12" r="3" fill="currentColor" />
      <circle cx="5" cy="19" r="3" fill="currentColor" />
    </svg>
  );
}

function FolderRowInner({
  rawId, name, isExpanded, onToggle,
  onAskRenameFolder, onAskCreateFolder, onCreateRequest, onDeleteFolder, onImportCurl,
}: {
  rawId: string;
  name: string;
  isExpanded: boolean;
  onToggle: () => void;
  onAskRenameFolder: (id: string, current: string) => void;
  onAskCreateFolder: (parentId: string | null) => void;
  onCreateRequest: (folderId: string | null) => void;
  onDeleteFolder: (id: string, name: string) => void;
  onImportCurl: (folderId: string | null) => void;
}) {
  return (
    <div className="group flex items-center gap-2 px-1 py-0.5 rounded text-text w-full min-w-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        onPointerDown={(e) => e.stopPropagation()}
        className="shrink-0 grid place-items-center rounded text-text-faint hover:text-text"
        aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
        title={isExpanded ? "Collapse" : "Expand"}
      >
        <FolderIcon open={isExpanded} />
      </button>
      {/* Click the name to toggle, double-click to rename. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        onDoubleClick={(e) => { e.stopPropagation(); onAskRenameFolder(rawId, name); }}
        className="flex-1 min-w-0 text-left truncate text-sm cursor-pointer select-none"
        title={name}
      >{name}</button>
      <RowMenu
        onAddRequest={() => onCreateRequest(rawId)}
        onAddFolder={() => onAskCreateFolder(rawId)}
        onRename={() => onAskRenameFolder(rawId, name)}
        onImportCurl={() => onImportCurl(rawId)}
        onDelete={() => onDeleteFolder(rawId, name)}
      />
    </div>
  );
}

// Single vs. double click on rows that *also* live inside a dnd-kit
// sortable tree. The native `dblclick` event gets swallowed when the
// drag activation distance is small, so we count clicks ourselves
// with a short timer.
function useSingleOrDoubleClick(onSingle: () => void, onDouble: () => void) {
  const timerRef = useRef<number | null>(null);
  return (e: React.MouseEvent) => {
    if (e.detail >= 2 || timerRef.current != null) {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      e.preventDefault();
      onDouble();
      return;
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      onSingle();
    }, 220);
  };
}

function RequestRowInner({ rawId, name, method, url, selected, workspaceId }: { rawId: string; name: string; method?: string; url?: string; selected: boolean; workspaceId: string }) {
  const nav = useNavigate();
  const rename = useRenameRequest();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const remove = useMutation({
    mutationFn: () => api.requests.remove(rawId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["request", rawId] });
      // Drop the dead tab from URL + localStorage. If we were viewing
      // the deleted request, jump to the next-active tab the helper
      // picked (the one to the right, or the new rightmost). Falling
      // back to home only when nothing else is open.
      // Namespaced tab IDs (`r:` / `w:`) — webhooks share the list.
      const { search, nextActive } = closeOpenTabFor(workspaceId, `r:${rawId}`);
      if (selected) {
        let pathname = "/";
        if (nextActive) {
          if (nextActive.startsWith("w:")) pathname = `/w/${nextActive.slice(2)}`;
          else if (nextActive.startsWith("r:")) pathname = `/r/${nextActive.slice(2)}`;
          else pathname = `/r/${nextActive}`;
        }
        nav({ pathname, search });
      } else if (search !== window.location.search) {
        nav({ pathname: window.location.pathname, search }, { replace: true });
      }
    },
  });
  // Duplicate — copy the full request detail then create a sibling.
  // Client-side so we don't need a new server endpoint.
  const duplicate = useMutation({
    mutationFn: async () => {
      const { request: src } = await api.requests.get(rawId);
      const created = await api.requests.create({
        collection_id: src.collection_id,
        name: `${src.name} copy`,
        method: src.method,
        url: src.url,
        headers: src.headers,
        query: src.query,
        body_type: src.body_type,
        body: src.body,
      });
      // create() doesn't accept folder_id directly; if the source
      // lived inside a folder, move the new one to match.
      if (src.folder_id) {
        await api.requests.move(created.id, { folder_id: src.folder_id });
      }
      return created.id;
    },
    onSuccess: (newId) => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      nav({ pathname: `/r/${newId}`, search: window.location.search });
    },
  });
  return (
    <div
      className={
        "group flex items-center gap-2 px-1 py-0.5 rounded text-sm w-full min-w-0 " +
        (selected
          ? "bg-accent-soft text-accent-bright"
          : "text-text-dim")
      }
      onClick={useSingleOrDoubleClick(
        () => nav({ pathname: `/r/${rawId}`, search: window.location.search }),
        () => rename(rawId, name),
      )}
      title={`${url || name} — double-click to rename`}
    >
      <span className={"method-tag shrink-0 " + (method ?? "GET").toLowerCase()}>{method ?? "GET"}</span>
      <span className="flex-1 min-w-0 truncate">{name}</span>
      <RowMenu
        onRename={() => rename(rawId, name)}
        onDuplicate={() => duplicate.mutate()}
        onDelete={async () => {
          const ok = await confirm({ title: `Delete "${name}"?`, description: "This permanently removes the request and all its revisions and runs.", danger: true });
          if (ok) remove.mutate();
        }}
      />
    </div>
  );
}

// Sidebar row for a webhook. Distinguished from request rows by the
// lightning-bolt glyph (no method tag — webhooks are inbound, multi-
// method by design). Click → navigate; double-click → rename.
function WebhookRowInner({ rawId, name, token, requestCount, selected, workspaceId, collectionId }: {
  rawId: string;
  name: string;
  token: string;
  requestCount: number;
  selected: boolean;
  workspaceId: string;
  collectionId: string;
}) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const remove = useMutation({
    mutationFn: () => api.webhooks.remove(rawId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhooks", collectionId] });
      const { search, nextActive } = closeOpenTabFor(workspaceId, `w:${rawId}`);
      if (selected) {
        let path = "/";
        if (nextActive) {
          if (nextActive.startsWith("w:")) path = `/w/${nextActive.slice(2)}`;
          else if (nextActive.startsWith("r:")) path = `/r/${nextActive.slice(2)}`;
          else path = `/r/${nextActive}`;
        }
        nav({ pathname: path, search });
      } else if (search !== window.location.search) {
        nav({ pathname: window.location.pathname, search }, { replace: true });
      }
    },
  });
  const duplicate = useMutation({
    mutationFn: async () => {
      // Duplicate: copy the user-facing config, mint a fresh token via
      // create(), then PATCH the response + forward fields to match.
      const { webhook: src } = await api.webhooks.get(rawId);
      const created = await api.webhooks.create({
        collection_id: src.collection_id,
        name: `${src.name} copy`,
        folder_id: src.folder_id,
      });
      await api.webhooks.update(created.id, {
        description: src.description,
        docs: src.docs,
        resp_status: src.resp_status,
        resp_content_type: src.resp_content_type,
        resp_body: src.resp_body,
        forward_enabled: src.forward_enabled,
        forward_url: src.forward_url,
        forward_method: src.forward_method,
        forward_transform: src.forward_transform,
      });
      return created.id;
    },
    onSuccess: (newId) => {
      qc.invalidateQueries({ queryKey: ["webhooks", collectionId] });
      nav({ pathname: `/w/${newId}`, search: window.location.search });
    },
  });
  const renameMut = useMutation({
    mutationFn: (next: string) => api.webhooks.update(rawId, { name: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhook", rawId] });
      qc.invalidateQueries({ queryKey: ["webhooks", collectionId] });
    },
  });
  const askRename = async () => {
    const next = await prompt({ title: "Rename webhook", placeholder: "Webhook name", initialValue: name, confirmLabel: "Rename", inputLabel: "Name" });
    if (next && next !== name) renameMut.mutate(next);
  };
  return (
    <div
      className={
        "group flex items-center gap-2 px-1 py-0.5 rounded text-sm w-full min-w-0 cursor-pointer " +
        (selected ? "bg-accent-soft text-accent-bright" : "text-text-dim")
      }
      onClick={useSingleOrDoubleClick(
        () => nav({ pathname: `/w/${rawId}`, search: window.location.search }),
        askRename,
      )}
      title={`/i/${token} — double-click to rename`}
    >
      <span className="shrink-0 text-accent" aria-hidden="true"><WebhookIcon /></span>
      <span className="flex-1 min-w-0 truncate">{name}</span>
      <RowMenu
        onRename={askRename}
        onDuplicate={() => duplicate.mutate()}
        onCopyUrl={async () => {
          await copyToClipboard(`${window.location.origin}/i/${token}`, "Copied webhook URL");
        }}
        onDelete={async () => {
          const ok = await confirm({ title: `Delete "${name}"?`, description: "This permanently removes the webhook and every captured request.", danger: true });
          if (ok) remove.mutate();
        }}
      />
      {/* Count sits on the far right edge. RowMenu lives before it so
          the (initially-hidden) 3-dot affordance still appears between
          the name and the count on hover, but the count itself is the
          last visible element regardless. */}
      {requestCount > 0 && (
        <span className="chip bg-surface-3 muted-strong shrink-0 text-[10px]">{requestCount}</span>
      )}
    </div>
  );
}

// Dropdown menu used by the top-of-sidebar "+" affordance. Three actions
// kept compact via on-click-outside dismiss. Anchored to the trigger so
// it lives in normal flow rather than a portal.
// Compact mode picker for the top-left of the sidebar. Replaces a
// 3-tab row that didn't fit when the sidebar was narrow. Menu is
// portaled to <body> with viewport-clamped positioning so it can't
// be clipped by the narrow sidebar's overflow-hidden. Keyboard:
// Enter/Space/↓ opens, ↑/↓ moves cursor, Home/End jump, Enter
// selects, Esc closes.
function SidebarModeDropdown({ activeTab, onPick }: { activeTab: SidebarTab; onPick: (t: SidebarTab) => void }) {
  const tabs: Array<{ id: SidebarTab; label: string; icon: ReactNode }> = [
    { id: "requests", label: "Requests", icon: <ModeRequestsIcon /> },
    { id: "webhooks", label: "Webhooks", icon: <ModeWebhooksIcon /> },
    { id: "flows",    label: "Flows",    icon: <ModeFlowsIcon /> },
  ];
  const active = tabs.find((t) => t.id === activeTab)!;
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [cursor, setCursor] = useState(() => tabs.findIndex((t) => t.id === activeTab));
  const MENU_W = 208;
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const top = r.bottom + 4;
    let left = r.left;
    const min = 8;
    const max = window.innerWidth - MENU_W - 8;
    if (left < min) left = min;
    if (left > max) left = Math.max(min, max);
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (btnRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    if (open) setCursor(Math.max(0, tabs.findIndex((t) => t.id === activeTab)));
  }, [open, activeTab]);

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape")        { e.preventDefault(); setOpen(false); btnRef.current?.focus(); }
    else if (e.key === "ArrowDown"){ e.preventDefault(); setCursor((c) => (c + 1) % tabs.length); }
    else if (e.key === "ArrowUp")  { e.preventDefault(); setCursor((c) => (c - 1 + tabs.length) % tabs.length); }
    else if (e.key === "Home")     { e.preventDefault(); setCursor(0); }
    else if (e.key === "End")      { e.preventDefault(); setCursor(tabs.length - 1); }
    else if (e.key === "Enter")    { e.preventDefault(); onPick(tabs[cursor].id); setOpen(false); btnRef.current?.focus(); }
  };

  return (
    <div className="flex items-center" onKeyDown={onKey}>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Sidebar mode: ${active.label}`}
        onClick={() => setOpen((v) => !v)}
        className="h-6 -ml-1 pl-2 pr-1.5 flex items-center gap-1.5 rounded text-sm font-medium text-text hover:bg-surface-2 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft min-w-0"
        title={`${active.label} — click to switch`}
      >
        <span className="text-accent shrink-0" aria-hidden="true">{active.icon}</span>
        <span className="truncate">{active.label}</span>
        <ChevronDownSmall />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          aria-label="Sidebar mode"
          className="card shadow-[var(--shadow-strong)] p-1 text-sm"
          style={{ position: "fixed", top: pos.top, left: pos.left, width: MENU_W, zIndex: 60 }}
        >
          {tabs.map((t, i) => (
            <button
              key={t.id}
              type="button"
              role="option"
              aria-selected={t.id === activeTab}
              onMouseEnter={() => setCursor(i)}
              onMouseDown={(e) => { e.preventDefault(); onPick(t.id); setOpen(false); btnRef.current?.focus(); }}
              className={
                "w-full flex items-center gap-2 px-2 py-1 rounded text-left " +
                (i === cursor ? "bg-surface-2 text-text" : "text-text-dim hover:text-text")
              }
            >
              <span className="text-accent w-4 grid place-items-center shrink-0" aria-hidden="true">{t.icon}</span>
              <span className="flex-1">{t.label}</span>
              {t.id === activeTab && <span className="text-accent-bright" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

function ChevronDownSmall() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0 text-text-faint">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// 12px-square mode glyphs — match the row icons in vibe (send arrow,
// lightning bolt, branching graph) so the dropdown reads as the
// "kind selector" for what's listed below.
function ModeRequestsIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function ModeWebhooksIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}
function ModeFlowsIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7.5 6 L16.5 11M16.5 13 L7.5 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="5" cy="5" r="3" fill="currentColor" />
      <circle cx="19" cy="12" r="3" fill="currentColor" />
      <circle cx="5" cy="19" r="3" fill="currentColor" />
    </svg>
  );
}

// Header "+" for the Requests tab — two actions (New request / New
// folder). Mirrors the Webhooks/Flows tabs' single-action buttons in
// look, but expands into a small menu when clicked.
function NewRequestMenu({ onNewRequest, onNewFolder, onImport }: { onNewRequest: () => void; onNewFolder: () => void; onImport: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        title="New…"
        aria-label="New…"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 100)}
        className="h-6 w-6 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
      ><PlusIcon /></button>
      {open && (
        <div className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-40 text-sm w-48" role="menu">
          <button
            className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
            onMouseDown={(e) => { e.preventDefault(); onNewRequest(); setOpen(false); }}
          >New request</button>
          <button
            className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
            onMouseDown={(e) => { e.preventDefault(); onNewFolder(); setOpen(false); }}
          >New folder</button>
          <div className="border-t border-border my-1" />
          <button
            className="w-full text-left px-2 py-1 rounded hover:bg-surface-2"
            onMouseDown={(e) => { e.preventDefault(); onImport(); setOpen(false); }}
          >Import collection…</button>
        </div>
      )}
    </div>
  );
}

function WebhookIcon() {
  // Lightning bolt — signals "inbound event", distinct from the request
  // method-tag glyph (which carries the HTTP verb on a request row).
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" fill="currentColor" />
    </svg>
  );
}

function FolderIcon({ open = false }: { open?: boolean }) {
  if (open) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
        {/* Open folder: back panel + slanted front lid */}
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v1.5H3V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M3 10.5h18.5l-1.7 7.2A2 2 0 0 1 17.86 19H5.14a2 2 0 0 1-1.94-1.5L3 10.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="currentColor" fillOpacity="0.08" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="currentColor" fillOpacity="0.06" />
    </svg>
  );
}
function FolderPlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 11v5M9.5 13.5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// Per-row overflow menu. Only the actions whose handlers were
// supplied show up — folder rows get Add request / Add folder, and
// request rows only get Rename / Delete (etc.).
function RowMenu({
  onAddRequest, onAddWebhook, onAddFolder, onRename, onDuplicate, onCopyUrl, onDelete, onImportCurl, onOpenApi, onExportOpenApi,
}: {
  onAddRequest?: () => void;
  onAddWebhook?: () => void;
  onAddFolder?: () => void;
  onRename?: () => void;
  onDuplicate?: () => void;
  onCopyUrl?: () => void;
  onDelete?: () => void;
  onImportCurl?: () => void;
  onOpenApi?: () => void;
  onExportOpenApi?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Menu is portaled to <body> so the overflow:hidden on the sidebar's
  // scroll container can't clip it. Position is computed from the
  // trigger's bounding rect each time it opens — and re-clamped to the
  // viewport so it never spills off-screen on a narrow sidebar.
  const MENU_W = 192;
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const top = r.bottom + 4;
    let left = r.right - MENU_W;
    const min = 8;
    const max = window.innerWidth - MENU_W - 8;
    if (left < min) left = min;
    if (left > max) left = Math.max(min, max);
    setPos({ top, left });
  }, [open]);

  // Build the visible items in render-order so keyboard nav, click,
  // and ARIA all stay in sync. Each entry knows its label, optional
  // danger styling, and what to do on activation.
  const items: Array<{ label: string; run: () => void; danger?: boolean }> = [];
  if (onAddRequest)    items.push({ label: "Add request",    run: onAddRequest });
  if (onAddWebhook)    items.push({ label: "Add webhook",    run: onAddWebhook });
  if (onAddFolder)     items.push({ label: "Add folder",     run: onAddFolder });
  if (onRename)        items.push({ label: "Rename",         run: onRename });
  if (onDuplicate)     items.push({ label: "Duplicate",      run: onDuplicate });
  if (onCopyUrl)       items.push({ label: "Copy URL",       run: onCopyUrl });
  if (onImportCurl)    items.push({ label: "Import curl…",   run: onImportCurl });
  if (onOpenApi)       items.push({ label: "Import OpenAPI…", run: onOpenApi });
  if (onExportOpenApi) items.push({ label: "Export OpenAPI", run: onExportOpenApi });
  if (onDelete)        items.push({ label: "Delete",         run: onDelete, danger: true });

  // Move focus to the first menu item on open; restore it to the trigger
  // on close so the keyboard journey is round-trip.
  useEffect(() => {
    if (!open || !menuRef.current) return;
    const first = menuRef.current.querySelector<HTMLButtonElement>('[role="menuitem"]');
    first?.focus();
  }, [open]);

  // Close when the user clicks anywhere outside the trigger or menu.
  // Replaces the old onBlur-on-trigger timeout, which closed the menu
  // the moment we moved keyboard focus into it.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (menuRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const closeAndRestoreFocus = () => {
    setOpen(false);
    btnRef.current?.focus();
  };

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!menuRef.current) return;
    const els = Array.from(menuRef.current.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'));
    const idx = els.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") {
      e.preventDefault(); e.stopPropagation();
      closeAndRestoreFocus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = els[(idx + 1 + els.length) % els.length];
      next?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = els[(idx - 1 + els.length) % els.length];
      prev?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      els[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      els[els.length - 1]?.focus();
    } else if (e.key === "Tab") {
      // Let Tab actually move focus, but the menu closes so the user
      // doesn't end up trapped inside.
      setOpen(false);
    }
  };

  return (
    <div onPointerDown={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onKeyDown={(e) => {
          // ArrowDown on the trigger opens + focuses the first item —
          // matches the platform convention for menu buttons.
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        className="h-6 w-6 grid place-items-center rounded text-text-faint opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent hover:text-text hover:bg-surface-3"
        title="More"
        aria-label="More actions"
      >
        <DotsIcon />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          className="card shadow-[var(--shadow-strong)] p-1 text-sm text-text"
          style={{ position: "fixed", top: pos.top, left: pos.left, width: MENU_W, zIndex: 60 }}
          role="menu"
          aria-orientation="vertical"
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className={
                "w-full text-left px-2 py-1 rounded hover:bg-surface-2 focus:bg-surface-2 focus:outline-none " +
                (it.danger ? "text-danger" : "")
              }
              onClick={() => { it.run(); closeAndRestoreFocus(); }}
            >{it.label}</button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

// Back-compat alias — CollectionNode still uses this name for the
// collection-level overflow menu in non-flat mode.
const CollectionMenu = RowMenu;

function Chevron({ expanded }: { expanded: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform 120ms ease" }}>
      <path d="M7 5l6 5-6 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}
